import { Action, configureStore, ThunkAction } from "@reduxjs/toolkit";
import {
  Client,
  FRRBid,
  ClientGOSettings,
  GOCertificates,
} from "api/models/Client";
import { Operator } from "api/models/Operator";
import { ReportsOptions, ReportsQuery } from "api/models/Reports";
import { Forecast, ForecastTemplate } from "api/models/Forecast";
import appReducer from "./appSlice";
import { User } from "api/models/User";
import { Contract, PortfolioContract } from "api/models/Contract";
import { MeteringPoint } from "api/models/MeteringPoint";
import {
  ProductionPark,
  ProductionParkLimitation,
} from "api/models/ProductionPark";
import { ProductionParkComponent } from "api/models/ProductionParkComponent";
import { ForecaCoefficientsData } from "api/models/Foreca";
import {
  ProductionForecast,
  ProductionForecastGroup,
  ProductionForecastNew,
} from "api/models/ProductionForecast";
import { Me } from "api/models/Me";
import { LitgridLog } from "api/models/Litgrid";
import { InviteTokenData } from "api/models/Invite";
import {
  SupplyPlanBaseLoads,
  SupplyPlanForecast,
  SupplyPlanPriceLimits,
  SupplyPlanScheduled,
} from "api/models/SupplyPlan";
import { Portfolio, PriceLimit } from "api/models/Portfolio";
import { SupplyPlan } from "api/models/NordPool";
import {
  GOGroup,
  GOYearlySummary,
  GOSummary,
  LastGOSyncTime,
  GOActiveClient,
} from "api/models/GO";
import { BatteryGroup } from "api/models/BatteryGroup";
import { Battery } from "api/models/Battery";
import { BalancePlan } from "api/models/BalancePlan";

export type AppProps = {
  invites: InviteTokenData[];
  me: Me;
  currentRoleClientId: number;
  clients: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: Client[];
  };
  client: Client;
  clientMeteringPoints: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: MeteringPoint[];
  };
  users: User[];
  user: User;
  operators: Operator[];
  operator: Operator;
  meteringPoints: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: MeteringPoint[];
  };
  contracts: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: Contract[];
  };
  reportsQueryOptions: ReportsOptions;
  reportsQueries: ReportsQuery[];
  forecastTemplates: ForecastTemplate[];
  forecasts: Forecast[];
  forecast: Forecast;
  productionPark: ProductionPark;
  productionParks: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: ProductionPark[];
  };
  productionParkComponent: ProductionParkComponent;
  productionParkComponents: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: ProductionParkComponent[];
  };
  productionForecast: ProductionForecast;
  productionForecasts: ProductionForecast[];
  productionForecastSelect: ProductionForecast[];
  forecaCoefficients: ForecaCoefficientsData[];
  litgridLogs: LitgridLog[];
  supplyPlanForecast: SupplyPlanForecast;
  clientSupplyPlanForecast: SupplyPlanForecast;
  supplyPlanPriceLimits: SupplyPlanPriceLimits;
  clientSupplyPlanPriceLimits: SupplyPlanPriceLimits;
  baseLoads: SupplyPlanBaseLoads;
  supplyPlanScheduled: SupplyPlanScheduled[];
  frrBids: FRRBid[];
  productionForecastGroups: ProductionForecastGroup[];
  productionForecastGroup: ProductionForecastGroup;
  productionForecastNew: ProductionForecastNew;
  productionParkLimitations: ProductionParkLimitation[];
  portfolios: Portfolio[];
  portfolio: Portfolio;
  portfolioPriceLimits: PriceLimit[];
  nordPoolSupplyPlans: SupplyPlan[];
  nordPoolSupplyPlan: SupplyPlan;
  nordPoolPriceSteps: number[];
  portfolioContracts: PortfolioContract[];
  clientGOSettings: ClientGOSettings;
  GOCertificates: GOCertificates;
  GOGroups: GOGroup[];
  GOYearlySummary: GOYearlySummary;
  GOSummary: GOSummary;
  lastGoSyncTime: LastGOSyncTime;
  GOActiveClients: GOActiveClient[];
  batteryGroups: BatteryGroup[];
  batteryGroup: BatteryGroup;
  batteries: Battery[];
  battery: Battery;
  balancePlans: BalancePlan[];
};

export type Thunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;

export const store = configureStore({
  reducer: {
    app: appReducer,
  },
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
